import { LoadingState, View } from "components/Shared";
import { Graph } from "components/Widgets";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import React, { memo, useEffect, useState } from "react";

/**
 * THE AMENDMENT TREE, DRAWN — how the lease got from its Original Lease to the
 * unit set it has today, and where the current-term pointer sits.
 *
 * Rendered with the SAME `components/Widgets/Graph` the workflow and template
 * pages use (dagre for layout, svg.js to draw, TB/LR toggle and Save-as-PDF for
 * free), so this file only has to turn amendments into its `nodes` / `edges`
 * contract:
 *
 *   nodes  [{ data: { id, label, className, phase: { id, color }, pin, url } }]
 *   edges  [{ data: { id, source, target } }]
 *
 * `phase.color` is what Graph fills a box with — see NODE_COLOURS. `pin` and
 * `url` are OPTIONAL props added to that widget for this picture (Sim, 25 Aug)
 * and passed by nothing else, so the workflow and template graphs are unchanged:
 * `pin` draws a marker in the box's top-left corner, `url` makes the box open
 * that address in a NEW TAB when clicked.
 *
 * WHAT THE PICTURE SAYS, and nothing more:
 *   the pin      this node has been the lease's current term (#movesPointer).
 *   the rule     a dashed line across the picture wherever a new term starts, so
 *                the bands either side of it are the old term and the new one.
 *   red          cancelled.
 *   black        a Termination — the end of the lease, drawn ABOVE all of it.
 *   grey         a Modification — a restatement, not a step.
 * Everything else is a plain box.
 *
 * TWO NODES ARE PLACED RATHER THAN FOUND, and neither claims a parent/child link
 * the fold does not have: a TERMINATION, lifted to the top with the lease hanging
 * off it on a DASHED edge, and anything with NO BaseAmendmentID at all (an
 * Assignment), which FLOATS at the left margin of the term it was executed
 * during — no line, no slot in the layout.
 *
 * THE POINTER CHAIN RUNS STRAIGHT DOWN. Each pointer is drawn in the column of
 * the pointer above it (Sim, 25 Aug), which is `alignColumns` — the pairs this
 * file builds during the walk, and Graph honours by replacing dagre's own
 * across-rank positions. #centreOnPointer then decides which side each branch
 * falls on.
 *
 * ---------------------------------------------------------------------------
 * THE EDGES MUST FORM A FOREST, OR DAGRE CAN HANG
 * ---------------------------------------------------------------------------
 * The tree is linked on Yardi's `base_amendment_id` STRING, and Yardi data can
 * contain a base that points back into its own subtree. `dagre.layout` is not
 * safe on a cycle, so the traversal below marks a node BEFORE descending into
 * it — the same guard `Amendment#subtree` uses server-side — and only emits an
 * edge for a parent/child pair it reached that way. Anything a cycle would have
 * stranded is emitted as its own root instead, so no amendment is ever dropped
 * from the picture.
 *
 * MIND THE TWO FAMILIES OF ID. Nodes are keyed on `amendment.id` (TCP's uuid,
 * which is what dagre and the SVG element ids use); the tree is LINKED on
 * `amendment_id` / `base_amendment_id` (Yardi's strings). Compared as strings
 * throughout, because the wire can send `2864224` and `"2864224"` for the same
 * lease.
 */

interface Props {
	agreement: tcpinpoint.LeaseAgreement;
	name?: string;
	icon?: string;
}

/**
 * TWO COLOURS, AND EVERYTHING ELSE IS PLAIN (Sim, 25 Aug).
 *
 * The tree used to fill four ways — current term, main tree, branch, cancelled —
 * which meant colour was carrying three unrelated facts at once and the eye had
 * nothing to rest on. It now says only the two things that change how a node
 * should be READ, and cancelled wins because it is the more urgent of the two:
 *
 *   cancelled     called off. Still in the history, grants nothing, its project
 *                 is closed — so every date and unit under it is a might-have-been.
 *   modification  changes no units and creates no project. It is a restatement
 *                 sitting in the tree, not a step in it.
 *
 * The CURRENT TERM is no longer coloured. It is a pin now, like every other node
 * that has moved the pointer — see #movesPointer.
 */
const NODE_COLOURS = {
	// $red from src/scss/_variables.scss. Dark enough that Graph flips the label
	// to white on its own (it runs the fill through Color#isDark).
	cancelled: "#fc4036",
	// The lease ENDS here (Sim, 25 Aug). Black because it is the one node that is
	// not a step in the lease's life but the end of it — and dark enough that the
	// label and the pin both come back white without any extra work.
	termination: "#1f1f1f",
	modification: "#d9d9d9",
	plain: "#ffffff"
};

const yardiKey = (amendment: tcpinpoint.Amendment): string =>
	String(_.isNil(amendment.amendment_id) ? amendment.id : amendment.amendment_id);

/**
 * A node that has been the lease's current term — the pointer chain. `moves_pointer`
 * is the API's own answer (Amendment::TypeBehaviour): Original Lease, Renewal and
 * Termination move the pointer; everything else hangs off the chain without
 * advancing it. These get the pin, and #centreOnPointer pulls them to the middle.
 *
 * NOT narrowed to the CURRENT current term (Sim, 25 Aug): the pin marks the
 * spine, and the live term is the last pin down it.
 */
const movesPointer = (amendment: tcpinpoint.Amendment): boolean => amendment.moves_pointer === true;

const typeIs = (amendment: tcpinpoint.Amendment, type: string) =>
	_.toLower(_.toString(amendment.amendment_type)) === type;

// Cancelled first: a cancelled Termination reads as cancelled, because "called
// off" is the more urgent fact about it.
const colourFor = (amendment: tcpinpoint.Amendment) => {
	if (!!amendment.cancelled) return { key: "cancelled", color: NODE_COLOURS.cancelled };
	if (typeIs(amendment, "termination")) return { key: "termination", color: NODE_COLOURS.termination };
	if (typeIs(amendment, "modification")) return { key: "modification", color: NODE_COLOURS.modification };
	return { key: "plain", color: NODE_COLOURS.plain };
};

/**
 * Siblings reordered so the pointer child sits in the MIDDLE of the row, and the
 * branches fall either side of it.
 *
 * This decides ORDER ONLY. It does NOT place the column — dagre reorders a rank
 * itself and throws the insertion order away (see Graph's #alignColumns), so the
 * pointer is put in its parent's column there, by position. What this still
 * decides is which side each branch lands on, because the tidy pass walks the
 * children in the order the edges were pushed.
 */
const centreOnPointer = (children: tcpinpoint.Amendment[]): tcpinpoint.Amendment[] => {
	const pointers = _.filter(children, movesPointer);
	if (_.isEmpty(pointers) || pointers.length === children.length) return children;

	// ROUND, not floor: with three siblings either side of it that puts the
	// pointer at index 2 of 4 rather than 1, i.e. as close to the middle as an
	// even row allows.
	const others = _.reject(children, movesPointer);
	const half = Math.round(others.length / 2);
	return [..._.take(others, half), ...pointers, ..._.drop(others, half)];
};

/**
 * amendments -> Graph's nodes/edges. Cycle-safe by construction; see the file
 * header.
 */
const buildGraph = (
	amendments: tcpinpoint.Amendment[],
	leaseAgreementId: string,
	currentAmendmentUuid?: string | null
) => {
	const byYardiId: { [key: string]: tcpinpoint.Amendment } = {};
	_.forEach(amendments, amendment => {
		byYardiId[yardiKey(amendment)] = amendment;
	});

	const childrenOf: { [key: string]: tcpinpoint.Amendment[] } = {};
	const roots: tcpinpoint.Amendment[] = [];
	const parentless: tcpinpoint.Amendment[] = [];
	// A TERMINATION IS DRAWN ABOVE THE WHOLE LEASE (Sim, 25 Aug), not as one more
	// child of the node it names — it is the end of the lease, and reading it
	// first is the point. It is LIFTED here: kept out of the tree on the way in,
	// then made the root below, with the real root(s) hanging off it. A CANCELLED
	// one is not lifted; a termination that was called off did not end anything.
	const terminations: tcpinpoint.Amendment[] = [];
	_.forEach(amendments, amendment => {
		if (typeIs(amendment, "termination") && !amendment.cancelled) {
			terminations.push(amendment);
			return;
		}
		const base = _.isNil(amendment.base_amendment_id) ? "" : String(amendment.base_amendment_id);
		// NO BASE AT ALL is a different thing from an unresolvable one. An
		// Assignment carries none — Amendment::TypeBehaviour marks its
		// BaseAmendmentID "present on the payload, deliberately unused" — so it has
		// nowhere in the tree and used to float off on its own as a second root.
		// Sim (25 Aug): "there is no baseamendmentid for assignment, so we just make
		// it in the term which is the latest pointer."
		if (!base) {
			parentless.push(amendment);
			return;
		}
		// A base Yardi never sent, or one that is not on this lease, is still a ROOT
		// here rather than a dropped node — unchanged, and NOT the case above.
		if (base === yardiKey(amendment) || !byYardiId[base]) {
			roots.push(amendment);
			return;
		}
		childrenOf[base] = [...(childrenOf[base] || []), amendment];
	});

	// uuids of the nodes drawn where this file PUT them rather than where the data
	// said — the lease under a Termination, and anything hung off a term below.
	// Their incoming edge is drawn DASHED, so the picture never claims a
	// parent/child link the fold does not have.
	const placed: { [id: string]: boolean } = {};
	// The subset of `placed` that is NOT IN THE TREE at all (Sim, 25 Aug: "the
	// assignment does not need to be within the tree, it can be at the left").
	// Its edge exists only so dagre ranks it into the right term's band; the edge
	// is never drawn, it takes no slot in the layout, and it is parked at the left
	// margin — so the term's own chain still runs straight down under its pointer.
	const floating: { [id: string]: boolean } = {};

	/**
	 * The pointer and everything above it, by uuid — walked up the bases, and
	 * guarded against a cycle. Everything on this chain is a real root or a real
	 * child and must stay where it is; a parentless node that is NOT on it is the
	 * one with nowhere to go. The Original Lease is on it, which is what keeps the
	 * root a root: it has no base either.
	 */
	const pointerChain: { [id: string]: boolean } = {};
	let climbing = !!currentAmendmentUuid ? _.find(amendments, { id: currentAmendmentUuid }) : undefined;
	while (!!climbing && !pointerChain[climbing.id]) {
		pointerChain[climbing.id] = true;
		const base = _.isNil(climbing.base_amendment_id) ? "" : String(climbing.base_amendment_id);
		climbing = byYardiId[base];
	}

	/**
	 * WHEN AN AMENDMENT WAS EXECUTED, as something sortable.
	 *
	 * **Yardi issues AmendmentIDs in order, so the id IS the sequence** — and for
	 * an Assignment it is the only signal that means anything: its dates are the
	 * lease's dates, not a statement about itself, so ordering by
	 * `amendment_start_date` put Sim's Assignment back in the Original Lease's term
	 * when he had told us it happened during the Renewal. `created_at` breaks a tie
	 * and covers an id that is not a number.
	 */
	const executedAtOrBefore = (node: tcpinpoint.Amendment, other: tcpinpoint.Amendment) => {
		const ours = Number(node.amendment_id);
		const theirs = Number(other.amendment_id);
		if (_.isFinite(ours) && _.isFinite(theirs) && ours !== theirs) return ours < theirs;

		return String(_.get(node, "created_at", "")) <= String(_.get(other, "created_at", ""));
	};

	/**
	 * WHICH TERM WAS RUNNING WHEN THIS ARRIVED (Sim, 25 Aug: "i need it to be the
	 * correct renewal area based on when the assignment is executed and which
	 * current pointer it is").
	 *
	 * The terms ARE the pointer nodes, so the answer is the LAST pointer executed
	 * at or before this node. A lifted Termination is not a candidate — it is drawn
	 * above the whole lease, so hanging anything off it would put that node at the
	 * top of the picture rather than inside a term.
	 *
	 * Falls back to the earliest term when nothing preceded it, and to nothing at
	 * all when the lease has no pointer — in which case the caller leaves the node
	 * as its own root, which is where it used to always land.
	 */
	const lifted: { [id: string]: boolean } = {};
	_.forEach(terminations, termination => (lifted[termination.id] = true));

	const terms = _.filter(amendments, node => movesPointer(node) && !node.cancelled && !lifted[node.id]);
	const termsInOrder = terms.slice().sort((a, b) => (executedAtOrBefore(a, b) ? -1 : 1));

	const termRunningAt = (amendment: tcpinpoint.Amendment) =>
		_.last(_.filter(termsInOrder, term => executedAtOrBefore(term, amendment))) || _.first(termsInOrder);

	_.forEach(parentless, amendment => {
		const term = termRunningAt(amendment);
		if (!term || pointerChain[amendment.id]) {
			roots.push(amendment);
			return;
		}
		placed[amendment.id] = true;
		floating[amendment.id] = true;
		// The edge exists ONLY to rank it into this term's band — Graph keeps a
		// floating node out of the layout and parks it at the left margin, so where
		// it sits among these children does not decide anything.
		childrenOf[yardiKey(term)] = [amendment, ...(childrenOf[yardiKey(term)] || [])];
	});

	// The lift itself: the first termination becomes the root and everything that
	// WAS a root hangs off it. Any others follow it down, so none is dropped.
	// APPENDED, never assigned — a parentless node may already have been hung off
	// this very termination above, and overwriting the list dropped it out of the
	// tree entirely (which is how an Assignment ended up back at the top).
	if (!!terminations.length) {
		const top = _.first(terminations) as tcpinpoint.Amendment;
		const adopted = [..._.tail(terminations), ...roots];
		_.forEach(adopted, child => (placed[child.id] = true));
		childrenOf[yardiKey(top)] = [...(childrenOf[yardiKey(top)] || []), ...adopted];
		roots.length = 0;
		roots.push(top);
	}

	const seen: { [key: string]: boolean } = {};
	// [anchor, node] uuid pairs: "draw this pointer in that pointer's column".
	// Built during the walk rather than after the fact because the anchor is the
	// nearest pointer ANCESTOR — a Renewal hanging off an Expansion still belongs
	// in its grandparent pointer's column, and a flat list cannot tell you that.
	const columns: string[][] = [];
	// The forest, settled: each node's children in the order they will be drawn,
	// and the nodes in pre-order. Taken FIRST, because the rank each edge needs
	// (below) is a question about a whole subtree, which one top-down pass cannot
	// answer as it goes.
	const orderedChildren: { [id: string]: tcpinpoint.Amendment[] } = {};
	const preOrder: tcpinpoint.Amendment[] = [];

	const walk = (amendment: tcpinpoint.Amendment, pointerAncestorId: string | null) => {
		const key = yardiKey(amendment);
		seen[key] = true;
		preOrder.push(amendment);
		const children = centreOnPointer(_.filter(childrenOf[key] || [], child => !seen[yardiKey(child)]));
		// Marked before descending, so a cycle cannot re-enter.
		_.forEach(children, child => {
			seen[yardiKey(child)] = true;
		});
		orderedChildren[amendment.id] = children;
		_.forEach(children, child => {
			const pointer = movesPointer(child);
			if (pointer && !!pointerAncestorId) columns.push([pointerAncestorId, child.id]);
			walk(child, pointer ? child.id : pointerAncestorId);
		});
	};

	// Roots first, then anything a cycle stranded, so every amendment is drawn.
	_.forEach([...roots, ...amendments], amendment => {
		if (!seen[yardiKey(amendment)]) walk(amendment, movesPointer(amendment) ? amendment.id : null);
	});

	/**
	 * A POINTER CHILD DROPS BELOW EVERYTHING ITS SIBLINGS REACH (Sim, 25 Aug), not
	 * merely one rank below the siblings themselves: a new term starting has to
	 * read as a new row, and a row it shares with the tail of the old term's branch
	 * is not one. So the Renewal on Sim's tree clears the Expansion AND the two
	 * nodes hanging off that Expansion.
	 *
	 * `minlen` is dagre's own "how many ranks apart must these two be", so this
	 * costs no layout of our own — it just has to be COMPUTED rather than fixed:
	 *
	 *   depth(node)    how many ranks the subtree below it occupies, which already
	 *                  accounts for any minlen inside it.
	 *   minlen(child)  1, unless the child is a pointer WITH non-pointer siblings,
	 *                  in which case it is far enough to clear the deepest of them.
	 *
	 * Computed over the reversed pre-order, which is a valid post-order for a
	 * forest: a parent always precedes its children, so every child is resolved by
	 * the time its parent is reached. An only child keeps `minlen` 1 — it is
	 * already alone on its row, and pushing it down would open an empty band.
	 */
	const depth: { [id: string]: number } = {};
	const minlen: { [id: string]: number } = {};
	_.forEachRight(preOrder, amendment => {
		const children = orderedChildren[amendment.id] || [];
		const clearance = _.max(_.map(_.reject(children, movesPointer), child => 1 + (depth[child.id] || 0))) || 0;
		_.forEach(children, child => {
			minlen[child.id] = movesPointer(child) && clearance > 0 ? clearance + 1 : 1;
		});
		depth[amendment.id] = _.max(_.map(children, child => minlen[child.id] + (depth[child.id] || 0))) || 0;
	});

	const edges: any[] = _.flatMap(preOrder, amendment =>
		_.map(orderedChildren[amendment.id] || [], child => ({
			data: {
				id: `${amendment.id}-${child.id}`,
				source: amendment.id,
				target: child.id,
				minlen: minlen[child.id],
				// PLACED, not found. The Termination and the lease it ended get a
				// DASHED line; a FLOATING node gets no line at all and no place in the
				// tree — it descends from nothing, and both a line and a slot would say
				// otherwise. It still needs an EDGE, because dagre ranks off edges and
				// a node with none would land on the top row instead of in its term.
				dashed: !!placed[child.id] && !floating[child.id],
				hidden: !!floating[child.id]
			}
		}))
	);

	const nodes = _.map(amendments, amendment => {
		const colour = colourFor(amendment);
		return {
			data: {
				id: amendment.id,
				// One line — Graph draws the label with a single svg text(), so it has no
				// second row to put units on. The units per node are on the Amendments
				// tab; this picture is about the SHAPE.
				label: `${amendment.amendment_type || translate("unknown")} #${amendment.amendment_id}`,
				className: `amendment amendment-${colour.key}`,
				phase: { id: colour.key, color: colour.color },
				// The corner glyph and the click target — both optional props on the
				// shared widget, and this is the only graph in the app that passes
				// either, so the workflow and template pictures are unchanged.
				pin: movesPointer(amendment),
				url: `/lease-agreements/${leaseAgreementId}/amendments/${amendment.id}`
			}
		};
	});

	// A rule is drawn above each node that starts a new term, which is exactly the
	// set of pointers with a pointer above them — the same pairs #alignColumns
	// takes, read for their target.
	return {
		nodes,
		edges,
		columns,
		dividers: _.map(columns, pair => pair[1]),
		floats: _.keys(floating)
	};
};

const LegendSwatch = ({ label, color }: { label: string; color: string }) => (
	<span style={{ display: "inline-flex", alignItems: "center", marginRight: "1.5rem" }}>
		<span
			style={{
				display: "inline-block",
				width: "0.9rem",
				height: "0.9rem",
				marginRight: "0.4rem",
				backgroundColor: color,
				border: "1px solid #ccc",
				borderRadius: "2px"
			}}
		/>
		{translate(label)}
	</span>
);

/**
 * The pin, in the legend, so the corner glyph does not have to be guessed at.
 * A font-awesome `<i>` rather than the SVG path Graph draws — the picture is
 * svg.js's, the legend is the page's, and matching them exactly is not worth
 * exporting a path from the widget.
 */
const LegendPin = ({ label }: { label: string }) => (
	<span style={{ display: "inline-flex", alignItems: "center", marginRight: "1.5rem" }}>
		<i className="fas fa-map-marker-alt" style={{ marginRight: "0.4rem" }} />
		{translate(label)}
	</span>
);

const TreeTab = ({ agreement }: Props) => {
	// null = no response yet. Driven from our own state rather than
	// `datasource.isLoading`: with no `renderTrigger` the datasource cannot
	// re-render this component when it flips isLoading back to false.
	const [amendments, setAmendments] = useState<tcpinpoint.Amendment[] | null>(null);
	const [datasource] = useState(new Datasource({ mainModelName: "amendment" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setAmendments(response.normalizedMainModelResponse || [])
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (_.isEmpty(agreement.id)) return;
		// One page: a lease's tree is a handful of nodes and the picture is only
		// honest if it has all of them.
		datasource.get("v2", `lease-agreements/${agreement.id}/amendments`, {
			queryStringParameters: { per_page: 1000 }
		});
	}, [datasource, agreement.id]);

	if (amendments === null) return <LoadingState />;

	if (_.isEmpty(amendments)) return <View>{translate("lease_agreement_no_amendments")}</View>;

	const { nodes, edges, columns, dividers, floats } = buildGraph(
		amendments,
		agreement.id,
		agreement.current_amendment_uuid
	);

	return (
		<View>
			{/* Not `muted` (Sim, 25 Aug): the legend is the key to the picture, so it
			    has to be readable rather than a footnote. */}
			<div style={{ marginBottom: "0.75rem", fontSize: "0.8rem", fontWeight: 600 }}>
				<LegendPin label="amendment_legend_pointer" />
				<LegendSwatch label="amendment_legend_cancelled" color={NODE_COLOURS.cancelled} />
				<LegendSwatch label="amendment_legend_termination" color={NODE_COLOURS.termination} />
				<LegendSwatch label="amendment_legend_modification" color={NODE_COLOURS.modification} />
			</div>
			{/* Keyed on the node count so Graph redraws when the tree changes — it
			    draws in componentDidMount / componentWillReceiveProps and appends into
			    a ref, so remounting is the reliable way to replace the picture. */}
			<Graph
				key={`tree-${nodes.length}-${edges.length}`}
				nodes={nodes}
				edges={edges}
				alignColumns={columns}
				dividers={dividers}
				floats={floats}
			/>
		</View>
	);
};

export default memo(TreeTab);
